/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import EventEmitter from 'eventemitter3';

import ScheduleContent, { ContentType } from './schedule-content';
import ScheduleOrphan from './schedule-orphan';

// TODO: Obecný konstruktor vs detekce obsahu ve statické funkci fromContent
// je odlišně než konstruktory ostatní ScheduleStrategy. Pro regiony je to
// takto nutné a asi je to i obecnější, i když se pak liší (ne)ošetření sirotků
// a z toho důvodu by měl být konstruktor de facto privátní a fromContent hlavní.
// Takže pak zvážit předělání všude jinde či refaktoring createStrategies
// v Schedule{Layout,Manager}.
/**
 * Strategie přehrávání pro region s obsahem.
 * @implements {ScheduleStrategy}
 */
class ScheduleRegion {
  /**
   * Vytvoří instanci pro danou scheduleStrategy. Tento sémanticky privátní
   * konstruktor by se neměl typicky použít, protože pro detekci podle obsahu
   * a ošetření "sirotků" je nutné typicky použít {@link fromContent}.
   * @param {Content} content (Ne)polymorfní obsah regionu.
   * @param {ScheduleStrategy} scheduleStrategy Podřízená strategie přehrávání.
   * @returns {ScheduleRegion} Nová instance.
   */
  constructor(content, scheduleStrategy) {
    this.emitter = new EventEmitter();
    // TODO: Používá se jen pro toString(), odstranit?
    this.content = content;
    this.scheduleStrategy = scheduleStrategy;
    this.volume = ScheduleRegion.detectVolume(content);
    // Navázání naslouchání na instanci.
    this.onGone = this.onGone.bind(this);
    this.onPlayed = this.onPlayed.bind(this);
    // Naslouchání událostem.
    scheduleStrategy.on('gone', this.onGone);
    scheduleStrategy.on('played', this.onPlayed);
  }

  /** @inheritdoc */
  destroy() {
    const { scheduleStrategy } = this;
    scheduleStrategy.off('gone', this.onGone);
    scheduleStrategy.off('played', this.onPlayed);
    scheduleStrategy.destroy();
  }

  /**
   * Detekuje hlasitost pro region.
   * @param {Content} content (Ne)polymorfní obsah.
   * @private
   * @returns {Fraction} Hlasitost regionu.
   */
  static detectVolume(content) {
    switch (ScheduleContent.type(content)) {
      case ContentType.Empty:
        return 0;
      case ContentType.Layout:
        // TODO: Šablony nyní explicitní výchozí hlasitost nemají.
        // Po zavedení změnit na return content.layout.volume.
        return 1;
      case ContentType.Medium:
        // TODO: Pozor, volume je podle deklarace v medium-attrs.js volitelná položka!
        return content.medium.volume;
      case ContentType.Playlist:
        // TODO: Pozor, volume je podle deklarace v playlist-attrs.js volitelná položka!
        return content.playlist.volume;
      // Kvůli uspokojení eslint pravidla default-case.
      default:
        throw ScheduleContent.errorDefaultCase(content);
    }
  }

  /**
   * Vytvoří instanci detekcí (ne)polymorfního obsahu a ošetří "sirotky".
   * Pokud ošetření sirotků není chtěné (např. speciální použití v ScheduleManager),
   * je nutné volat konstruktor.
   * @param {Content} content (Ne)polymorfní obsah.
   * @param {ContentInfo} contentInfo Informace o obsahu zařízení.
   * @param {ViewerRegistry} viewerRegistry Registr informací o typech médií.
   * @returns {ScheduleStrategy} Nová instance.
   */
  static fromContent(content, contentInfo, viewerRegistry) {
    let scheduleStrategy = ScheduleContent.detectStrategy(
      content,
      contentInfo,
      viewerRegistry
    );

    switch (ScheduleContent.type(content)) {
      case ContentType.Empty:
        // TODO #523: Je otázkou, zda je sémanticky nutné zaobalovat prázdný
        // obsah (ScheduleEmpty) ScheduleOrphan s délkou 0. Jediný pozitivní
        // efekt je ten, že ScheduleOrphan vyplní ScheduleInfo.playDuration,
        // takže případné šáhnutí na ScheduleInfo.getPlayDuration() nehodí
        // chybu na rozdíl toho, kdyby se položka nenaplnila. Avšak
        // ScheduleEmpty nenese médium, takže prakticky není důvod na tuto
        // položku šahat.
        // scheduleStrategy = new ScheduleOrphan(0, scheduleStrategy);
        break;
      case ContentType.Layout:
        scheduleStrategy = new ScheduleOrphan(
          // TODO: Pozor, playDuration je podle deklarace v layout-attrs.js volitelná položka!
          // Ale v SchedulePlayer.play je tranzitivně ošetřeno, že undefined, null nebo 0
          // se považují za nekonečnou délku, ale je to prasárna a spoléhání vedlejší efekt.
          content.layout.playDuration,
          scheduleStrategy
        );
        break;
      case ContentType.Medium:
        scheduleStrategy = new ScheduleOrphan(
          // TODO: Pozor, playDuration je podle deklarace v medium-attrs.js volitelná položka!
          // Ale v SchedulePlayer.play je tranzitivně ošetřeno, že undefined, null nebo 0
          // se považují za nekonečnou délku, ale je to prasárna a spoléhání vedlejší efekt.
          content.medium.playDuration,
          scheduleStrategy
        );
        break;
      case ContentType.Playlist:
        break;
      // Kvůli uspokojení eslint pravidla default-case.
      default:
        throw ScheduleContent.errorDefaultCase(content);
    }

    return new ScheduleRegion(content, scheduleStrategy);
  }

  // TODO #523: Pozor na to, že mergování playDuration se děje v ScheduleOrphan,
  // které ví explicitně předanou playDuration v konstruktoru. Podle dospecifikace
  // sémantiky přehrávání by měl region řešit výchozí volume a taky délku. Jenže
  // výjimkou je playlist, který nic takového jako explicitně nastavitelnou délku
  // v Ki-Wi Serveru nemá (a až později se pro playlist začalo vracet 0, protože
  // dřív tam bylo hausnumero). Takže pak je vlastně v pořádku a chtěné, že to
  // počítá ScheduleOrphan, který se používá pro samostatné médium nebo šablonu,
  // tj. nikoliv pro playlist.
  /**
   * Určí informace pro přehrání média.
   * @param {ScheduleInfo} scheduleInfo Informace z podřízené strategie.
   * @private
   * @returns {ScheduleInfo} Informace pro přehrání média.
   */
  getInfo(scheduleInfo) {
    if (scheduleInfo.volume === 0 || this.volume === 1) {
      return scheduleInfo;
    }

    return scheduleInfo.merge({
      volume: this.volume
    });
  }

  /** @inheritdoc */
  go() {
    const scheduleInfo = this.scheduleStrategy.go();
    return this.getInfo(scheduleInfo);
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na přechod na další slajd.
   * @param {ScheduleInfo} scheduleInfo Informace potřebné k přehrání slajdu, na které byl proveden přechod.
   * @private
   * @returns {undefined} Nic.
   */
  onGone(scheduleInfo) {
    this.emitter.emit('gone', this.getInfo(scheduleInfo));
  }

  /**
   * Reakce na přehrání slajdu.
   * @param {ScheduleInfo} scheduleInfo Informace o slajdu, který byl přehrán.
   * @private
   * @returns {undefined} Nic.
   */
  onPlayed(scheduleInfo) {
    this.emitter.emit('played', this.getInfo(scheduleInfo));
  }

  /** @inheritdoc */
  plan(offset) {
    return this.scheduleStrategy
      .plan(offset)
      .map((scheduleInfo) => this.getInfo(scheduleInfo));
  }

  /** @inheritdoc */
  skip() {
    this.scheduleStrategy.skip();
  }

  /** @inheritdoc */
  stop() {
    this.scheduleStrategy.stop();
  }

  /** @inheritdoc */
  toString() {
    return `ScheduleRegion {content: ${ScheduleContent.toString(
      this.content
    )}, volume: ${this.volume}}`;
  }
}

export default ScheduleRegion;
